import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ShoppingBag, ArrowRight, ShieldCheck, HeartHandshake, PhoneCall, Award, MessageCircle, Search, X, ChevronRight, Home, Grid, User, Sparkles, ExternalLink, UserPlus } from 'lucide-react';
import { Product, CartItem, OrderRecord, DistributorConfig, BannerSlide } from './types';
import { PRODUCTS, DISTRIBUTOR_INFO, CATEGORIES } from './data/products';
import { DEFAULT_BANNER_SLIDES } from './data/banners';
import { db, doc, getDoc, setDoc, auth, onAuthStateChanged, signOut, secureGetDoc, onSnapshot } from './firebase';
import { Navbar } from './components/Navbar';
import { HeroBannerSlider } from './components/HeroBannerSlider';
import { BannerUploadModal } from './components/BannerUploadModal';
import { ProductSyncModal } from './components/ProductSyncModal';
import { CategoryNav } from './components/CategoryNav';
import { PromotionalBanners } from './components/PromotionalBanners';
import { ProductCard } from './components/ProductCard';
import { CartDrawer } from './components/CartDrawer';
import { CheckoutModal } from './components/CheckoutModal';
import { SalesSlipModal } from './components/SalesSlipModal';
import { GoogleSheetsAdminModal } from './components/GoogleSheetsAdminModal';
import { PolicyModal } from './components/PolicyModal';
import { AuthModal } from './components/AuthModal';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { PinVerificationModal } from './components/PinVerificationModal';
import { ResetPinModal } from './components/ResetPinModal';
import { CustomerDashboardModal } from './components/CustomerDashboardModal';
import { SellerDashboardModal } from './components/SellerDashboardModal';
import { SellerDashboardView } from './components/SellerDashboardView';
import { SellerAdminGate } from './components/SellerAdminGate';
import { MemberDashboardView } from './components/MemberDashboardView';
import { ProductDetailPage } from './components/ProductDetailPage';
import { AboutUsPage } from './components/AboutUsPage';
import { CheckoutPageView } from './components/CheckoutPageView';
import { QRPaymentProcessingView } from './components/QRPaymentProcessingView';
import { ChatAndShopView } from './components/ChatAndShopView';
import { GiffarineMemberPage } from './components/GiffarineMemberPage';
import { PrivacyPolicyPage, PolicyTabType } from './components/PrivacyPolicyPage';
import { MemberRoomView } from './components/MemberRoomView';
import { ArticlePageView } from './components/ArticlePageView';
import { GIFFARINE_OFFICIAL_LOGO } from './assets/logo';
import { seedAddressesToCloud } from './lib/thaiAddressesDb';

export default function App() {
  const [distributor, setDistributor] = useState<DistributorConfig>(DISTRIBUTOR_INFO);
  
  // Authenticated user state
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isCustomerDashboardOpen, setIsCustomerDashboardOpen] = useState(false);
  const [isSellerDashboardOpen, setIsSellerDashboardOpen] = useState(false);

  // Security Reset PIN modal state from URL link
  const [resetPinData, setResetPinData] = useState<{
    isOpen: boolean;
    token: string;
    email: string;
  }>({
    isOpen: false,
    token: '',
    email: ''
  });

  // Routing: detect /member-XXXXXXXX route to display MemberDashboardView
  const [currentPathMemberId, setCurrentPathMemberId] = useState<string | null>(null);
  // Routing: detect /product/:slug (e.g. /product/84051_N1)
  const [currentProductSlug, setCurrentProductSlug] = useState<string | null>(null);
  // Routing: detect /checkout/:slug (e.g. /checkout/MEM1234-ORD20260914-A82K)
  const [currentCheckoutSlug, setCurrentCheckoutSlug] = useState<string | null>(null);
  // Routing: detect /checkout/:slug/qr-payment-processing
  const [isQrProcessingPage, setIsQrProcessingPage] = useState(false);
  // Routing: detect /aboutus page route
  const [isAboutUsPage, setIsAboutUsPage] = useState(false);
  // Routing: detect /seller/admin-dashboard page route
  const [isSellerAdminPage, setIsSellerAdminPage] = useState(false);
  // Routing: detect /chatandshop page route (Sub-path https://www.GIFFFORYOU.COM/chatandshop)
  const [isChatAndShopPage, setIsChatAndShopPage] = useState(false);
  // Routing: detect /member page route (Sub-path https://www.GIFFFORYOU.COM/member)
  const [isGiffarineMemberPage, setIsGiffarineMemberPage] = useState(false);
  // Routing: detect /privacy-policy and policy sub-routes
  const [isPrivacyPolicyPage, setIsPrivacyPolicyPage] = useState(false);
  const [policyPageTab, setPolicyPageTab] = useState<PolicyTabType>('privacy');
  // Routing: detect /memberroom page route
  const [isMemberRoomPage, setIsMemberRoomPage] = useState(false);
  // Routing: detect public SEO Article page slug (e.g. /สมัครกิฟฟารีน-ออนไลน์-pinkgiff or /article/:slug)
  const [currentArticleSlug, setCurrentArticleSlug] = useState<string | null>(null);

  useEffect(() => {
    const checkPath = () => {
      // Check for security Reset PIN URL queries: ?action=reset-pin&token=...&email=...
      const urlParams = new URLSearchParams(window.location.search);
      const action = urlParams.get('action');
      const token = urlParams.get('token');
      const email = urlParams.get('email');

      if (action === 'reset-pin' && token && email) {
        setResetPinData({
          isOpen: true,
          token,
          email: decodeURIComponent(email)
        });
      }

      const pathnameLower = window.location.pathname.toLowerCase().replace(/\/$/, '');
      if (pathnameLower === '/seller/admin-dashboard' || pathnameLower.startsWith('/seller/admin-dashboard')) {
        setIsSellerAdminPage(true);
        // Dynamic Anti-Bot Meta Robots Injection to prevent indexing
        let robotsMeta = document.querySelector('meta[name="robots"]');
        if (!robotsMeta) {
          robotsMeta = document.createElement('meta');
          robotsMeta.setAttribute('name', 'robots');
          document.head.appendChild(robotsMeta);
        }
        robotsMeta.setAttribute('content', 'noindex, nofollow, noarchive, nosnippet');
      } else {
        setIsSellerAdminPage(false);
        const robotsMeta = document.querySelector('meta[name="robots"]');
        if (robotsMeta) {
          robotsMeta.setAttribute('content', 'index, follow');
        }
      }

      if (pathnameLower === '/memberroom' || pathnameLower.startsWith('/memberroom')) {
        setIsMemberRoomPage(true);
      } else {
        setIsMemberRoomPage(false);
      }

      if (pathnameLower === '/aboutus') {
        setIsAboutUsPage(true);
      } else {
        setIsAboutUsPage(false);
      }

      if (
        pathnameLower === '/privacy-policy' ||
        pathnameLower === '/privacy' ||
        pathnameLower === '/pdpa' ||
        pathnameLower === '/cookie-policy' ||
        pathnameLower === '/cookie' ||
        pathnameLower === '/cookie-settings' ||
        pathnameLower === '/terms' ||
        pathnameLower === '/terms-of-service' ||
        pathnameLower === '/shipping-policy' ||
        pathnameLower === '/shipping'
      ) {
        setIsPrivacyPolicyPage(true);
        if (pathnameLower === '/pdpa') {
          setPolicyPageTab('pdpa');
        } else if (pathnameLower === '/cookie-policy' || pathnameLower === '/cookie') {
          setPolicyPageTab('cookie');
        } else if (pathnameLower === '/cookie-settings') {
          setPolicyPageTab('cookie-settings');
        } else if (pathnameLower === '/terms' || pathnameLower === '/terms-of-service') {
          setPolicyPageTab('terms');
        } else if (pathnameLower === '/shipping-policy' || pathnameLower === '/shipping') {
          setPolicyPageTab('shipping');
        } else {
          const tabParam = urlParams.get('tab') as PolicyTabType;
          if (tabParam && ['privacy', 'pdpa', 'cookie', 'cookie-settings', 'terms', 'shipping'].includes(tabParam)) {
            setPolicyPageTab(tabParam);
          } else {
            setPolicyPageTab('privacy');
          }
        }
      } else {
        setIsPrivacyPolicyPage(false);
      }

      if (pathnameLower === '/member') {
        window.history.replaceState({}, '', '/member-privilege');
        setIsGiffarineMemberPage(true);
      } else if (pathnameLower === '/member-privilege' || pathnameLower.startsWith('/member-privilege')) {
        setIsGiffarineMemberPage(true);
      } else {
        setIsGiffarineMemberPage(false);
      }

      if (pathnameLower === '/chatandshop' || pathnameLower.startsWith('/chatandshop')) {
        setIsChatAndShopPage(true);
      } else {
        setIsChatAndShopPage(false);
      }

      // Check for /checkout/:slug/qr-payment-processing first
      const qrProcessingMatch = window.location.pathname.match(/\/checkout\/([a-zA-Z0-9_-]+)\/qr-payment-processing\/?$/i);
      if (qrProcessingMatch) {
        setCurrentCheckoutSlug(qrProcessingMatch[1]);
        setIsQrProcessingPage(true);
      } else {
        setIsQrProcessingPage(false);
        const checkoutMatch = window.location.pathname.match(/\/checkout\/([a-zA-Z0-9_-]+)/i);
        if (checkoutMatch) {
          setCurrentCheckoutSlug(checkoutMatch[1]);
        } else {
          setCurrentCheckoutSlug(null);
        }
      }

      const memberMatch = window.location.pathname.match(/\/member-([a-zA-Z0-9]+)/);
      if (memberMatch) {
        setCurrentPathMemberId(memberMatch[1]);
      } else {
        setCurrentPathMemberId(null);
      }

      const productMatch = window.location.pathname.match(/\/product\/([a-zA-Z0-9_-]+)/);
      if (productMatch) {
        setCurrentProductSlug(productMatch[1]);
      } else {
        setCurrentProductSlug(null);
      }

      // Detect Article slug (e.g., /article/:slug or direct /:slug)
      const rawPath = window.location.pathname.replace(/\/$/, '');
      const decodedPath = decodeURIComponent(rawPath).replace(/^\//, '');

      const isKnownStaticRoute = 
        !decodedPath ||
        pathnameLower === '' ||
        pathnameLower === '/' ||
        pathnameLower === '/seller/admin-dashboard' ||
        pathnameLower.startsWith('/seller/admin-dashboard') ||
        pathnameLower === '/memberroom' ||
        pathnameLower.startsWith('/memberroom') ||
        pathnameLower === '/aboutus' ||
        pathnameLower === '/privacy-policy' ||
        pathnameLower === '/privacy' ||
        pathnameLower === '/pdpa' ||
        pathnameLower === '/cookie-policy' ||
        pathnameLower === '/cookie' ||
        pathnameLower === '/cookie-settings' ||
        pathnameLower === '/terms' ||
        pathnameLower === '/terms-of-service' ||
        pathnameLower === '/shipping-policy' ||
        pathnameLower === '/shipping' ||
        pathnameLower === '/member-privilege' ||
        pathnameLower.startsWith('/member-privilege') ||
        pathnameLower === '/chatandshop' ||
        pathnameLower.startsWith('/chatandshop') ||
        pathnameLower.startsWith('/checkout') ||
        pathnameLower.startsWith('/member-') ||
        pathnameLower.startsWith('/product/');

      if (!isKnownStaticRoute && decodedPath) {
        const cleanArticleSlug = decodedPath.replace(/^article\//i, '');
        setCurrentArticleSlug(cleanArticleSlug);
      } else {
        setCurrentArticleSlug(null);
      }
    };

    // Initial check
    checkPath();

    // Listen to push/popstate
    window.addEventListener('popstate', checkPath);
    return () => window.removeEventListener('popstate', checkPath);
  }, []);

  const handleOpenSellerDashboard = () => {
    window.history.pushState({}, '', '/seller/admin-dashboard');
    window.dispatchEvent(new Event('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenProduct = (product: Product) => {
    const slug = `${product.code}_N1`;
    setCurrentProductSlug(slug);
    window.history.pushState({}, '', `/product/${slug}`);
    window.dispatchEvent(new Event('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigateHome = () => {
    setIsSellerAdminPage(false);
    setCurrentProductSlug(null);
    setCurrentPathMemberId(null);
    setCurrentCheckoutSlug(null);
    setIsQrProcessingPage(false);
    setIsAboutUsPage(false);
    setIsGiffarineMemberPage(false);
    setIsChatAndShopPage(false);
    setCurrentArticleSlug(null);
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new Event('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigateCatalog = () => {
    setCurrentProductSlug(null);
    setCurrentCheckoutSlug(null);
    setIsQrProcessingPage(false);
    setCurrentArticleSlug(null);
    setSelectedCategory('all');
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new Event('popstate'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Transaction Security PIN Gate State
  const [isPinGateOpen, setIsPinGateOpen] = useState(false);
  const [pendingOrderSuccessData, setPendingOrderSuccessData] = useState<any>(null);
  const [isSellerOtpVerified, setIsSellerOtpVerified] = useState<boolean>(() => {
    return sessionStorage.getItem('seller_otp_verified') === 'true';
  });

  // Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: any) => {
      if (firebaseUser) {
        try {
          const userDocRef = doc(db, 'users', firebaseUser.uid);
          const userSnap = await secureGetDoc(userDocRef, `users/${firebaseUser.uid}`);
          const isMasterAdminEmail = (
            firebaseUser.email?.toLowerCase() === 'giffarine108037654@gmail.com' ||
            firebaseUser.email?.toLowerCase() === 'dlgiffarine@gmail.com' ||
            firebaseUser.email?.toLowerCase() === 'admin@pinkgiffarine.com'
          );

          if (userSnap && userSnap.exists()) {
            const data = userSnap.data() as any;
            setCurrentUser({
              ...data,
              role: isMasterAdminEmail ? 'admin' : (data?.role || 'customer'),
              customerId: data?.customerId ? String(data.customerId).trim() : ''
            });
          } else {
            const defaultRole = isMasterAdminEmail ? 'admin' : 'customer';
            setCurrentUser({
              id: firebaseUser.uid,
              email: firebaseUser.email,
              customerId: '',
              role: defaultRole
            });
          }
        } catch (err) {
          console.error('Error fetching auth user profile:', err);
          const isMasterAdminEmail = (
            firebaseUser.email?.toLowerCase() === 'giffarine108037654@gmail.com' ||
            firebaseUser.email?.toLowerCase() === 'dlgiffarine@gmail.com' ||
            firebaseUser.email?.toLowerCase() === 'admin@pinkgiffarine.com'
          );
          setCurrentUser({
            id: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Member',
            customerId: '',
            role: isMasterAdminEmail ? 'admin' : 'customer'
          });
        }
      } else {
        setCurrentUser(null);
        setIsSellerOtpVerified(false);
        sessionStorage.removeItem('seller_otp_verified');
      }
    });
    return () => unsubscribe();
  }, []);

  const [syncedProducts, setSyncedProducts] = useState<Product[]>(() => {
    try {
      const saved = localStorage.getItem('giffarine_synced_products');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const cleaned = parsed.filter((p: Product) => p.code !== '11014' && p.id !== 'giff-11014');
          return cleaned;
        }
      }
    } catch (e) {
      console.error(e);
    }
    return [];
  });

  const [banners, setBanners] = useState<BannerSlide[]>(() => {
    try {
      const saved = localStorage.getItem('giffarine_banners');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const hasUnsplash = parsed.some((b: BannerSlide) => b.imageUrl?.includes('unsplash.com'));
          if (!hasUnsplash) return parsed;
        }
      }
    } catch (e) {
      console.error(e);
    }
    return DEFAULT_BANNER_SLIDES;
  });

  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [categorySearchQueries, setCategorySearchQueries] = useState<Record<string, string>>({});

  const handleCategorySearchChange = (categoryId: string, query: string) => {
    setCategorySearchQueries(prev => ({
      ...prev,
      [categoryId]: query
    }));
  };

  // Modals state
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSlipOpen, setIsSlipOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isUploadBannerOpen, setIsUploadBannerOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [policyModalTab, setPolicyModalTab] = useState<'privacy' | 'terms' | 'shipping' | 'pdpa' | 'cookie'>('privacy');
  const [isCookieBannerOpen, setIsCookieBannerOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState<OrderRecord | null>(null);
  const activeBannersLoadedFromCloud = useRef(false);

  // Fetch distributor configuration and banners from backend
  useEffect(() => {
    fetch('/api/config')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setDistributor((prev) => ({ ...prev, ...data }));
        }
      })
      .catch((err) => console.log('Config loaded with default fallback', err));

    // Load persistent sliders/banners from server disk & Storage Bucket
    fetch('/api/sliders')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const slidesList = data?.sliders || data?.banners;
        if (slidesList && Array.isArray(slidesList) && slidesList.length > 0) {
          // If Firestore cloud sync already hydrated newer custom banners, skip overwriting
          if (!activeBannersLoadedFromCloud.current) {
            setBanners(slidesList);
            try {
              localStorage.setItem('giffarine_banners', JSON.stringify(slidesList));
            } catch (e) {}
          }
        }
      })
      .catch((err) => console.log('Sliders loaded with default fallback', err));

    // Cloud Firestore real-time synchronization & rehydration across server restarts
    try {
      const unsubBanners = onSnapshot(doc(db, 'site_settings', 'banners'), (snap) => {
        if (snap.exists()) {
          const cloudData = snap.data();
          if (cloudData?.banners && Array.isArray(cloudData.banners) && cloudData.banners.length > 0) {
            activeBannersLoadedFromCloud.current = true;
            setBanners(cloudData.banners);
            try {
              localStorage.setItem('giffarine_banners', JSON.stringify(cloudData.banners));
            } catch (e) {}
          }
        }
      }, (err) => {
        console.log('Firestore banners snapshot info:', err);
      });
      return () => unsubBanners();
    } catch (fInitErr) {
      console.log('Firestore init check:', fInitErr);
    }

    // Fetch server cached products
    fetch('/api/products/synced')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.products && Array.isArray(data.products) && data.products.length > 0) {
          setSyncedProducts((prev) => {
            const merged = [...prev];
            data.products.forEach((p: Product) => {
              if (!merged.some(m => m.id === p.id || m.code === p.code)) {
                merged.push(p);
              }
            });
            return merged;
          });
        }
      })
      .catch((err) => console.log('Synced products loaded', err));
  }, []);

  const handleProductSynced = (newProduct: Product) => {
    setSyncedProducts((prev) => {
      const filtered = prev.filter((p) => p.code !== newProduct.code);
      const updated = [newProduct, ...filtered];
      try {
        localStorage.setItem('giffarine_synced_products', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const handleRemoveSyncedProduct = (productId: string) => {
    setSyncedProducts((prev) => {
      const updated = prev.filter((p) => p.id !== productId);
      try {
        localStorage.setItem('giffarine_synced_products', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const handleSaveBanners = (updatedSlides: BannerSlide[]) => {
    setBanners(updatedSlides);
    try {
      localStorage.setItem('giffarine_banners', JSON.stringify(updatedSlides));
    } catch (e) {
      console.warn('LocalStorage quota limit exceeded, relying on server & firestore persistence', e);
    }

    // 1. Persist directly to server disk
    fetch('/api/banners', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ banners: updatedSlides }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log('[Banner System] Saved to server disk:', data);
      })
      .catch((err) => console.error('[Banner System] Server save error:', err));

    // 2. Persist to Cloud Firestore
    try {
      setDoc(doc(db, 'site_settings', 'banners'), {
        banners: updatedSlides,
        updatedAt: new Date().toISOString(),
      }).catch((fErr) => {
        console.log('[Banner System] Firestore cloud sync info:', fErr);
      });
    } catch (err) {
      console.log('[Banner System] Firestore init check:', err);
    }
  };

  const handleResetBanners = () => {
    setBanners(DEFAULT_BANNER_SLIDES);
    try {
      localStorage.removeItem('giffarine_banners');
    } catch (e) {}

    fetch('/api/banners', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ banners: DEFAULT_BANNER_SLIDES }),
    }).catch(() => {});

    try {
      setDoc(doc(db, 'site_settings', 'banners'), {
        banners: DEFAULT_BANNER_SLIDES,
        updatedAt: new Date().toISOString(),
      }).catch(() => {});
    } catch (e) {}
  };

  const handleQuickAddToCartByCode = (productCode: string) => {
    const found = PRODUCTS.find((p) => p.code === productCode);
    if (found) {
      handleAddToCart(found, 1);
      setIsCartOpen(true);
    }
  };

  // Cart operations
  const handleAddToCart = (product: Product, quantity: number = 1) => {
    const qty = typeof quantity === 'number' && !isNaN(quantity) && quantity > 0 ? quantity : 1;
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: (Number(item.quantity) || 0) + qty }
            : item
        );
      }
      return [...prev, { product, quantity: qty }];
    });
  };

  const handleUpdateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const handleRemoveItem = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  // Cart totals
  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0),
    [cart]
  );
  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + ((Number(item.product.memberPrice) || Number(item.product.price) || 0) * (Number(item.quantity) || 1)), 0),
    [cart]
  );

  // Filtered products (combined default catalog + safely synced products, excluding 11014)
  const allProducts = useMemo(() => {
    const defaultFiltered = PRODUCTS.filter(p => p.code !== '11014' && p.id !== 'giff-11014');
    const syncedFiltered = syncedProducts.filter(p => p.code !== '11014' && p.id !== 'giff-11014');
    return [...syncedFiltered, ...defaultFiltered.filter(p => !syncedFiltered.some(sp => sp.code === p.code))];
  }, [syncedProducts]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter((p) => {
      const matchesCategory =
        selectedCategory === 'all' || p.category === selectedCategory;
      const query = searchQuery.trim().toLowerCase();
      const matchesQuery =
        !query ||
        p.name.toLowerCase().includes(query) ||
        p.code.includes(query) ||
        p.description.toLowerCase().includes(query);
      return matchesCategory && matchesQuery;
    });
  }, [allProducts, selectedCategory, searchQuery]);

  // Grouped products for homepage (selectedCategory === 'all' and no searchQuery): 8 items per category
  const categorizedSections = useMemo(() => {
    // Only real product categories (exclude 'all')
    const categoryList = CATEGORIES.filter(c => c.id !== 'all');
    return categoryList.map(cat => {
      const items = allProducts.filter(p => p.category === cat.id);
      return {
        category: cat,
        totalCount: items.length,
        items: items.slice(0, 8),
      };
    }).filter(section => section.totalCount > 0);
  }, [allProducts]);

  // Handle successful order from CheckoutModal
  const handleOrderSuccess = (newOrder: OrderRecord) => {
    setActiveOrder(newOrder);
    setCart([]); // Clear cart
    setIsSlipOpen(true); // Pop up itemized Sales Receipt Slip with auto-download
  };

  // Redirect Checkout Flow: Create Draft Order & Redirect to /checkout/{member_id}-{order_id} without showing popup
  const handleProceedToDraftCheckout = async () => {
    if (cart.length === 0) return;
    try {
      const effectiveMemberId = currentUser?.customerId || currentUser?.memberId || currentPathMemberId || 'MEM' + Math.floor(100000 + Math.random() * 900000);
      const subtotal = cart.reduce((sum, item) => sum + item.product.memberPrice * item.quantity, 0);
      const deliveryFee = subtotal >= distributor.freeShippingThreshold ? 0 : distributor.shippingFee;
      const totalAmount = subtotal + deliveryFee;
      const totalPv = cart.reduce((sum, item) => sum + item.product.pv * item.quantity, 0);

      const res = await fetch('/api/orders/create-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: effectiveMemberId,
          email: currentUser?.email || '',
          customer_email: currentUser?.email || '',
          customer_name: currentUser?.fullName || '',
          cart_items: cart.map(item => ({
            code: item.product.code,
            name: item.product.name,
            price: item.product.memberPrice,
            quantity: item.quantity,
            pv: item.product.pv,
            image: item.product.image
          })),
          total_amount: totalAmount,
          subtotal,
          delivery_fee: deliveryFee,
          total_pv: totalPv
        })
      });

      const data = await res.json();
      if (res.ok && data.relative_checkout_url) {
        setIsCartOpen(false);
        // Seamless client-side navigation avoiding broken subdomains
        window.history.pushState({}, '', data.relative_checkout_url);
        window.dispatchEvent(new Event('popstate'));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        alert(data.error || 'ไม่สามารถสร้างคำสั่งซื้อได้ กรุณาลองใหม่อีกครั้ง');
      }
    } catch (err) {
      console.error('Create draft order error:', err);
      alert('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองใหม่อีกครั้ง');
    }
  };

  const isHeaderFooterHidden = isSellerAdminPage || Boolean(currentCheckoutSlug) || isQrProcessingPage || isChatAndShopPage || isGiffarineMemberPage || isMemberRoomPage;

  return (
    <div className={`min-h-screen flex flex-col font-['Prompt',sans-serif] relative ${isHeaderFooterHidden ? 'bg-white pb-0' : 'bg-white pb-[110px] md:pb-0'}`}>
      {/* Clean white base */}
      {!isHeaderFooterHidden && (
        <div className="fixed inset-0 bg-white pointer-events-none -z-1" />
      )}

      {/* Top Announcement Bar & Sticky Header (Only on regular customer store views) */}
      {!isHeaderFooterHidden && (
        <Navbar
          distributor={distributor}
          cartCount={cartCount}
          cartTotal={cartTotal}
          onOpenCart={() => setIsCartOpen(true)}
          onOpenAdmin={() => setIsAdminOpen(true)}
          onOpenUploadBanner={() => setIsUploadBannerOpen(true)}
          onOpenProductSync={() => setIsSyncModalOpen(true)}
          onOpenPolicy={() => setIsPolicyOpen(true)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelectCategory={setSelectedCategory}
          currentUser={currentUser}
          onOpenAuth={() => setIsAuthOpen(true)}
          onOpenCustomerDashboard={() => {
            if (currentUser?.customerId) {
              window.history.pushState({}, '', `/member-${currentUser.customerId}`);
              window.dispatchEvent(new Event('popstate'));
            } else {
              setIsAuthOpen(true);
            }
          }}
          onOpenSellerDashboard={handleOpenSellerDashboard}
          onLogout={async () => {
            await signOut(auth);
            setCurrentUser(null);
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new Event('popstate'));
          }}
        />
      )}

      {isChatAndShopPage ? (
        <ChatAndShopView
          onBackToStore={handleNavigateHome}
          onAddToCart={handleAddToCart}
          onOpenCart={() => setIsCartOpen(true)}
          cartCount={cartCount}
          customerUser={currentUser}
          cartItems={cart}
          cartTotal={cartTotal}
          onOpenCheckout={handleProceedToDraftCheckout}
          onProceedToCheckout={handleProceedToDraftCheckout}
          onOpenAuth={() => setIsAuthOpen(true)}
          onOpenProfile={() => {
            if (currentUser?.customerId) {
              window.history.pushState({}, '', `/member-${currentUser.customerId}`);
              window.dispatchEvent(new Event('popstate'));
            } else {
              setIsAuthOpen(true);
            }
          }}
        />
      ) : isSellerAdminPage ? (
        (currentUser && currentUser.email?.toLowerCase().trim() === 'giffarine108037654@gmail.com' && isSellerOtpVerified) ? (
          <SellerDashboardView
            onSelectOrderForSlip={(selected) => {
              setActiveOrder(selected);
              setIsSlipOpen(true);
            }}
            availableProducts={allProducts}
            onNavigateHome={handleNavigateHome}
            currentUser={currentUser}
            onLogout={async () => {
              await signOut(auth);
              setCurrentUser(null);
              setIsSellerOtpVerified(false);
              sessionStorage.removeItem('seller_otp_verified');
              window.history.pushState({}, '', '/');
              window.dispatchEvent(new Event('popstate'));
            }}
            onLogin={() => setIsAuthOpen(true)}
          />
        ) : (
          <SellerAdminGate
            currentUser={currentUser}
            onSuccess={(user) => {
              setCurrentUser(user);
              setIsSellerOtpVerified(true);
              sessionStorage.setItem('seller_otp_verified', 'true');
            }}
            onNavigateHome={handleNavigateHome}
          />
        )
      ) : isQrProcessingPage && currentCheckoutSlug ? (
        <QRPaymentProcessingView
          slug={currentCheckoutSlug}
          currentUser={currentUser}
          onNavigateHome={handleNavigateHome}
          onOpenAuth={() => setIsAuthOpen(true)}
        />
      ) : currentCheckoutSlug ? (
        <CheckoutPageView
          checkoutSlug={currentCheckoutSlug}
          currentUser={currentUser}
          onOpenAuth={() => setIsAuthOpen(true)}
          onNavigateHome={handleNavigateHome}
        />
      ) : currentPathMemberId ? (
        <MemberDashboardView
          memberId={currentPathMemberId}
          currentUser={currentUser}
          onOpenAuth={() => setIsAuthOpen(true)}
          onLogout={async () => {
            await signOut(auth);
            setCurrentUser(null);
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new Event('popstate'));
          }}
          distributor={distributor}
        />
      ) : isAboutUsPage ? (
        <AboutUsPage
          distributor={distributor}
          onNavigateHome={handleNavigateHome}
        />
      ) : isPrivacyPolicyPage ? (
        <PrivacyPolicyPage
          distributor={distributor}
          onNavigateHome={handleNavigateHome}
          initialTab={policyPageTab}
        />
      ) : isMemberRoomPage ? (
        <MemberRoomView
          onNavigateHome={handleNavigateHome}
          currentUser={currentUser}
        />
      ) : currentArticleSlug ? (
        <ArticlePageView
          slug={currentArticleSlug}
          onNavigateHome={handleNavigateHome}
          onOpenChatAndShop={() => {
            setCurrentArticleSlug(null);
            setIsChatAndShopPage(true);
            window.history.pushState({}, '', '/chatandshop');
            window.dispatchEvent(new Event('popstate'));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      ) : isGiffarineMemberPage ? (
        <GiffarineMemberPage
          onNavigateHome={handleNavigateHome}
          onOpenChatAndShop={() => {
            setIsGiffarineMemberPage(false);
            setIsChatAndShopPage(true);
            window.history.pushState({}, '', '/chatandshop');
            window.dispatchEvent(new Event('popstate'));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
        />
      ) : currentProductSlug ? (
        <ProductDetailPage
          slug={currentProductSlug}
          initialProduct={
            allProducts.find(
              (p) => p.code === currentProductSlug.replace(/_N[0-9]+$/i, '').trim()
            ) || null
          }
          onNavigateHome={handleNavigateHome}
          onNavigateCatalog={handleNavigateCatalog}
          onAddToCart={handleAddToCart}
        />
      ) : (
        <>
          {/* Hero Banner Slider (2-3 Slides with Direct Upload Feature as requested) */}
          <HeroBannerSlider
            slides={banners}
            onOpenUploadModal={() => setIsUploadBannerOpen(true)}
            onQuickAddToCart={handleQuickAddToCartByCode}
            onSaveSlides={handleSaveBanners}
          />

          {/* Main Content Area */}
          <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4 py-2 sm:py-3">
            {/* Category Navigation Shortcuts */}
            <CategoryNav
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />

            {/* Promotional Banner Grid */}
            <PromotionalBanners />

            {/* Product Catalog Section */}
            <div id="product-catalog-section" className="mt-8">
              {/* Unified Metallic Silver Glass Bubble Container (บับเบิ้ลแก้วสีเงินโลหะ ครอบทั้งหัวข้อและช่องค้นหา) */}
              <div className="relative rounded-[26px] sm:rounded-[30px] bg-[radial-gradient(ellipse_at_30%_25%,_#FFFFFF_0%,_#F8FAFC_35%,_#E2E8F0_70%,_#CBD5E1_100%)] border-2 border-white shadow-lg shadow-slate-400/20 backdrop-blur-md p-4 sm:p-5 mb-6 overflow-hidden transition-all duration-300">
                {/* 3D Specular Bubble Light Flare (Top Reflection) */}
                <div className="absolute top-1.5 left-4 right-4 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent opacity-90 pointer-events-none" />
                <div className="absolute bottom-1.5 right-8 w-32 h-1 rounded-full bg-white/60 blur-[0.6px] pointer-events-none" />

                {/* Header Section inside Silver Bubble */}
                <div className="relative z-10 flex flex-wrap items-center justify-between gap-3 mb-3.5 px-0.5">
                  <div>
                    <h2 className="text-base sm:text-lg font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-tr from-[#2A59FE] to-blue-400 shadow-xs animate-pulse shrink-0" />
                      <span>
                        {selectedCategory !== 'all'
                          ? `หมวดหมู่: ${CATEGORIES.find(c => c.id === selectedCategory)?.name || 'ผลิตภัณฑ์กิฟฟารีน'}`
                          : searchQuery
                          ? `ผลการค้นหา "${searchQuery}"`
                          : 'รายการผลิตภัณฑ์กิฟฟารีน'}
                      </span>
                      <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-blue-50 text-[#2A59FE] border border-blue-200/80 shadow-2xs">
                        {selectedCategory === 'all' && !searchQuery
                          ? `${allProducts.length} รายการ`
                          : `${filteredProducts.length} รายการ`}
                      </span>
                    </h2>
                  </div>

                  {selectedCategory !== 'all' && (
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className="text-xs text-[#2A59FE] hover:text-[#1E3A8A] font-bold hover:underline transition-colors"
                    >
                      ล้างตัวกรองหมวดหมู่
                    </button>
                  )}
                </div>

                {/* Search Input Box inside Silver Bubble */}
                <div className="relative z-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                  <div className="relative flex-1 flex items-center">
                    <div className="absolute left-3.5 sm:left-4 text-[#2A59FE] pointer-events-none">
                      <Search className="w-5 h-5" />
                    </div>
                    <input
                      type="text"
                      id="product-search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="ค้นหาชื่อสินค้า หรือ รหัสสินค้า เช่น 11001, คอลลาเจน, ยาสีฟัน..."
                      className="w-full pl-11 sm:pl-12 pr-10 py-3 bg-white/95 focus:bg-white text-slate-800 placeholder:text-slate-500 rounded-full text-sm font-medium border border-slate-200 focus:border-[#2A59FE] focus:ring-3 focus:ring-blue-300/30 shadow-inner focus:outline-none transition-all"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3.5 p-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                        title="ล้างคำค้นหา"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {selectedCategory !== 'all' && (
                    <button
                      type="button"
                      onClick={() => setSelectedCategory('all')}
                      className="shrink-0 px-4 py-3 rounded-full bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-bold transition-all shadow-xs hover:shadow flex items-center justify-center gap-1.5 active:scale-95"
                    >
                      <span>ล้างตัวกรองหมวดหมู่</span>
                      <X className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                  )}
                </div>
              </div>

              {/* Product Cards Grid */}
              {selectedCategory === 'all' && !searchQuery ? (
                /* หน้าแรก: ดีไซน์แสดงผลแยกหมวดหมู่ หมวดหมู่ละ 8 รายการ พร้อมช่องค้นหาประจำทุกหมวดหมู่ และปุ่มดูรายการสินค้าทั้งหมด */
                <div className="space-y-10 sm:space-y-12">
                  {categorizedSections.map(({ category, totalCount, items }) => {
                    const categoryQuery = (categorySearchQueries[category.id] || '').trim().toLowerCase();
                    const categoryAllProducts = allProducts.filter(p => p.category === category.id);
                    const displayItems = categoryQuery
                      ? categoryAllProducts.filter(p =>
                          p.name.toLowerCase().includes(categoryQuery) ||
                          p.code.includes(categoryQuery) ||
                          p.description.toLowerCase().includes(categoryQuery)
                        )
                      : items;

                    return (
                      <section
                        key={category.id}
                        id={`category-section-${category.id}`}
                        className="space-y-4"
                      >
                        {/* แถบหัวข้อหมวดหมู่ พร้อมช่องค้นหาสินค้าประจำหมวดหมู่ และปุ่มกดดูรายการสินค้าทั้งหมด */}
                        <div className="p-3.5 sm:p-4 rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200/80 shadow-xs space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 sm:gap-3">
                              <span className="w-2.5 h-2.5 rounded-full bg-slate-500 shadow-xs shrink-0" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="text-base sm:text-lg font-black text-slate-800 tracking-tight">
                                    {category.name}
                                  </h3>
                                  <span className="text-[11px] sm:text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200/80">
                                    {categoryQuery ? `${displayItems.length} จาก ${totalCount} รายการ` : `${totalCount} รายการ`}
                                  </span>
                                </div>
                                {categoryQuery && (
                                  <p className="text-[11px] sm:text-xs text-slate-500 mt-0.5">
                                    ผลการค้นหา "{categorySearchQueries[category.id]}" ในหมวด{category.name}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* ปุ่มดูทั้งหมดในหมวดหมู่นี้ */}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCategory(category.id);
                                window.scrollTo({ top: 400, behavior: 'smooth' });
                              }}
                              className="shrink-0 px-3.5 sm:px-4 py-2 rounded-full bg-slate-100/80 hover:bg-slate-800 text-slate-700 hover:text-white border border-slate-200 text-xs sm:text-sm font-bold transition-all shadow-2xs hover:shadow-md flex items-center gap-1 active:scale-95 cursor-pointer group"
                            >
                              <span>ดูทั้ง {totalCount} รายการ</span>
                              <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white group-hover:translate-x-0.5 transition-transform" />
                            </button>
                          </div>

                          {/* ช่องค้นหาสินค้าประจำหมวดหมู่นี้ (แสดงในทุกหมวดหมู่) */}
                          <div className="relative flex items-center">
                            <div className="absolute left-3.5 sm:left-4 text-slate-400 pointer-events-none">
                              <Search className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                            </div>
                            <input
                              type="text"
                              value={categorySearchQueries[category.id] || ''}
                              onChange={(e) => handleCategorySearchChange(category.id, e.target.value)}
                              placeholder={`ค้นหาซื้อสินค้าในหมวด ${category.name} หรือ รหัสสินค้า เช่น 11001, ชื่อสินค้า...`}
                              className="w-full pl-10 sm:pl-11 pr-10 py-2.5 sm:py-3 bg-slate-50/70 hover:bg-white focus:bg-white text-slate-800 placeholder:text-slate-400 rounded-full text-xs sm:text-sm font-medium border border-slate-200 focus:border-slate-500 focus:ring-3 focus:ring-slate-300/30 shadow-inner focus:outline-none transition-all"
                            />
                            {categorySearchQueries[category.id] && (
                              <button
                                type="button"
                                onClick={() => handleCategorySearchChange(category.id, '')}
                                className="absolute right-3.5 p-1 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                                title="ล้างคำค้นหาในหมวดนี้"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* รายการสินค้าของหมวดหมู่นี้ หรือ แจ้งเตือนกรณีค้นหาไม่พบ */}
                        {displayItems.length === 0 ? (
                          <div className="p-8 rounded-2xl bg-white/80 border border-dashed border-slate-300 text-center text-slate-500 shadow-2xs">
                            <p className="text-xs sm:text-sm font-semibold text-slate-700">
                              ไม่พบสินค้าที่ตรงกับคำค้นหา "{categorySearchQueries[category.id]}" ในหมวด{category.name}
                            </p>
                            <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
                              <button
                                type="button"
                                onClick={() => handleCategorySearchChange(category.id, '')}
                                className="px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all border border-slate-300 cursor-pointer"
                              >
                                ล้างคำค้นหาในหมวดนี้
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSearchQuery(categorySearchQueries[category.id]);
                                  window.scrollTo({ top: 400, behavior: 'smooth' });
                                }}
                                className="px-3.5 py-1.5 rounded-full bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
                              >
                                ค้นหาในทุกหมวดหมู่ ({allProducts.length} รายการ)
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
                            {displayItems.map((product) => (
                              <ProductCard
                                key={product.id}
                                product={product}
                                onAddToCart={handleAddToCart}
                                onProductClick={handleOpenProduct}
                              />
                            ))}
                          </div>
                        )}

                        {/* ปุ่มกดดูเพิ่มเติมด้านล่างหมวดหมู่ กรณีไม่มีการค้นหาและมีสินค้ามากกว่า 8 รายการ */}
                        {!categoryQuery && totalCount > 8 && (
                          <div className="text-center pt-1 pb-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCategory(category.id);
                                window.scrollTo({ top: 400, behavior: 'smooth' });
                              }}
                              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300 text-xs sm:text-sm font-bold shadow-xs hover:shadow transition-all active:scale-95 cursor-pointer"
                            >
                              <span>ดูสินค้าหมวด {category.name} ทั้งหมด ({totalCount} รายการ)</span>
                              <ArrowRight className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="bg-white/85 backdrop-blur-md rounded-2xl border border-slate-200/80 p-12 text-center text-slate-400 shadow-xs">
                  <p className="text-sm font-semibold text-slate-600">
                    ไม่พบสินค้าที่ตรงกับคำค้นหา "{searchQuery}"
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                    }}
                    className="mt-3 px-4 py-2 bg-slate-100 text-slate-700 border border-slate-300 rounded-xl text-xs font-semibold hover:bg-slate-200 transition-colors"
                  >
                    ดูสินค้าทั้งหมด
                  </button>
                </div>
              ) : (
                /* เมื่อเลือกหมวดหมู่ใดหมวดหมู่หนึ่งจาก CategoryNav หรือกำลังค้นหา: แสดงสินค้าทั้งหมดในหมวดนั้น */
                <div className="space-y-4">
                  {/* Banner แนะนำสมัครกิฟฟารีน เมื่อค้นหาคำว่า กิฟฟารีน หรือ สมัคร หรือ สมาชิก */}
                  {searchQuery && (searchQuery.includes('กิฟฟารีน') || searchQuery.includes('สมัคร') || searchQuery.includes('สมาชิก') || searchQuery.toLowerCase().includes('member') || searchQuery.toLowerCase().includes('giffarine')) && (
                    <div className="bg-gradient-to-r from-pink-500 via-[#E60067] to-rose-600 rounded-2xl p-4 sm:p-5 text-white shadow-md flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="space-y-1 text-center sm:text-left">
                        <div className="inline-flex items-center gap-1.5 text-xs font-black uppercase text-pink-100 bg-white/20 px-2.5 py-0.5 rounded-full">
                          <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                          <span>สมัครกิฟฟารีนออนไลน์ (180 บาทตลอดชีพ)</span>
                        </div>
                        <h4 className="font-extrabold text-sm sm:text-base text-white">
                          สนใจสมัครสมาชิกกิฟฟารีน รับส่วนลด 25% ทันทีตั้งแต่ชิ้นแรก?
                        </h4>
                        <p className="text-xs text-pink-100">
                          ไม่มีต่ออายุรายปี ไม่บังคับยอด พร้อมรับเงินปันผลคืน 10% - 25% และความคุ้มครองประกันอุบัติเหตุฟรี
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => {
                            window.history.pushState({}, '', '/member-privilege');
                            window.dispatchEvent(new Event('popstate'));
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="px-4 py-2 bg-white text-[#E60067] hover:bg-pink-50 rounded-xl text-xs font-bold shadow-xs transition-all cursor-pointer"
                        >
                          อ่านรายละเอียด
                        </button>
                        <a
                          href="https://www.giffarine.com/member/register/132000061"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-black/30 hover:bg-black/40 text-white rounded-xl text-xs font-bold border border-white/30 transition-all cursor-pointer flex items-center gap-1"
                        >
                          <span>สมัครทันที</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
                    {filteredProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        onAddToCart={handleAddToCart}
                        onProductClick={handleOpenProduct}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        </>
      )}

      {/* Floating Mobile Cart Summary Bar */}
      {cartCount > 0 && !isCartOpen && !isCheckoutOpen && !isSlipOpen && (
        <div className="fixed bottom-4 left-4 right-4 z-30 sm:hidden">
          <button
            onClick={() => setIsCartOpen(true)}
            className="w-full py-3.5 px-5 bg-gradient-to-r from-[#FF6B97] via-[#F43F85] to-[#DB2777] text-white rounded-2xl shadow-xl shadow-pink-900/20 border border-pink-300/40 flex items-center justify-between active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                {cartCount}
              </div>
              <div className="text-left leading-tight">
                <div className="text-xs font-bold">เปิดดูตะกร้าสินค้า</div>
                <div className="text-[11px] text-pink-100">
                  ฿{cartTotal.toLocaleString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs font-bold bg-white text-pink-700 px-3 py-1.5 rounded-xl shadow-xs">
              <span>สั่งซื้อ</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        distributor={distributor}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onProceedToCheckout={handleProceedToDraftCheckout}
      />

      {/* Checkout Modal (Address & 2 Payment Channels) */}
      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cart={cart}
        distributor={distributor}
        onOrderSuccess={handleOrderSuccess}
        currentUser={currentUser}
        onOpenAuth={() => setIsAuthOpen(true)}
      />

      {/* Instant 80mm Pop-up Sales Slip Modal with Auto-download */}
      <SalesSlipModal
        isOpen={isSlipOpen}
        onClose={() => setIsSlipOpen(false)}
        order={activeOrder}
        distributor={distributor}
      />

      {/* Google Sheets Schema & Admin Tracking Drawer */}
      <GoogleSheetsAdminModal
        isOpen={isAdminOpen}
        onClose={() => setIsAdminOpen(false)}
        onSelectOrderForSlip={(selected) => {
          setActiveOrder(selected);
          setIsSlipOpen(true);
        }}
      />

      {/* Banner Upload & Management Modal (2-3 Slides) */}
      <BannerUploadModal
        isOpen={isUploadBannerOpen}
        onClose={() => setIsUploadBannerOpen(false)}
        slides={banners}
        onSaveSlides={handleSaveBanners}
        onResetSlides={handleResetBanners}
      />

      {/* Product Sync & Cloud Cache Modal (Safe One-Time Sync & Embed) */}
      <ProductSyncModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
        onProductSynced={handleProductSynced}
        syncedProducts={syncedProducts}
        onRemoveSyncedProduct={handleRemoveSyncedProduct}
      />

      {/* Website Legal Policy & PDPA Modal for GIFFFORYOU.COM */}
      <PolicyModal
        isOpen={isPolicyOpen}
        onClose={() => setIsPolicyOpen(false)}
        distributor={distributor}
        defaultTab={policyModalTab}
      />

      {/* Cookie Consent & Preferences Banner */}
      <CookieConsentBanner
        isOpen={isCookieBannerOpen ? true : undefined}
        onClose={() => setIsCookieBannerOpen(false)}
        onOpenPolicyModal={(tab) => {
          setPolicyModalTab(tab || 'cookie');
          setIsPolicyOpen(true);
        }}
      />

      {/* Authentication and Registration Modal */}
      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onAuthSuccess={(userProfile) => {
          setCurrentUser(userProfile);
          setIsAuthOpen(false);
        }}
      />

      {/* Customer Profile & Transaction History Dashboard */}
      <CustomerDashboardModal
        isOpen={isCustomerDashboardOpen}
        onClose={() => setIsCustomerDashboardOpen(false)}
        user={currentUser}
      />

      {/* Seller and Admin Configuration Dashboard */}
      <SellerDashboardModal
        isOpen={isSellerDashboardOpen}
        onClose={() => setIsSellerDashboardOpen(false)}
        onSelectOrderForSlip={(selected) => {
          setActiveOrder(selected);
          setIsSlipOpen(true);
        }}
        availableProducts={allProducts}
      />

      {/* Security Reset 6-Digit PIN Modal from Email Link */}
      <ResetPinModal
        isOpen={resetPinData.isOpen}
        token={resetPinData.token}
        email={resetPinData.email}
        onClose={() => {
          setResetPinData({ isOpen: false, token: '', email: '' });
          // Clean URL without reload
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
        onSuccess={(newPin) => {
          if (currentUser) {
            setCurrentUser((prev: any) => ({ ...prev, pinHash: newPin, isPinSetup: true }));
          }
        }}
      />

      {/* Footer with clean light theme and exact Dark Navy Capsule Badge (Hidden on mobile, moved to Member Submenu) */}
      {!isHeaderFooterHidden && (
        <footer className="mt-16 relative bg-white border-t border-slate-200/80 text-slate-800 overflow-hidden hidden md:block">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 pb-8 border-b border-slate-200">
              
              {/* Column 1 (5 cols): Logo Capsule Badge & Core Navigation Links */}
              <div className="md:col-span-5 space-y-4">
                {/* Official Giffarine Pill Badge Capsule (Gray Background) */}
                <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-slate-600 shadow-sm shadow-slate-900/10 border border-slate-500">
                  <div className="w-6 h-6 rounded-md bg-white p-0.5 overflow-hidden flex items-center justify-center shrink-0 shadow-2xs">
                    <img
                      src="/icon.png"
                      alt="Giffarine Official App Icon"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="h-3.5 w-px bg-white/50" />
                  <img
                    src={GIFFARINE_OFFICIAL_LOGO}
                    alt="GIFFARINE"
                    className="h-4 w-auto object-contain pr-1 brightness-0 invert"
                  />
                </div>

                {/* Navigation & Policies List - Unified Direct Redirect Architecture */}
                <ul className="space-y-2 text-[13px] sm:text-sm font-medium">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/aboutus');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>เกี่ยวกับเรา (About Us)</span>
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/member-privilege');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 group-hover:bg-[#DB2777] transition-colors" />
                        <span className="text-[#DB2777]">สมัครกิฟฟารีน (180 บาทตลอดชีพ)</span>
                      </span>
                      <span className="text-[10px] bg-rose-50 text-rose-600 font-extrabold px-1.5 py-0.5 rounded border border-rose-200">
                        ลด 25%
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/privacy-policy');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>นโยบายความเป็นส่วนตัว (Privacy Policy)</span>
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/pdpa');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>การคุ้มครองข้อมูลส่วนบุคคล PDPA</span>
                      </span>
                      <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-1.5 py-0.5 rounded border border-emerald-200">
                        PDPA
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/cookie-policy');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>นโยบายการใช้คุกกี้ (Cookie Policy)</span>
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/cookie-settings');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>การตั้งค่าคุกกี้ (Cookie Settings)</span>
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/terms');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>เงื่อนไขและข้อตกลงการใช้บริการ</span>
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        window.history.pushState({}, '', '/shipping-policy');
                        window.dispatchEvent(new Event('popstate'));
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="w-full text-slate-700 hover:text-[#DB2777] text-left transition-all py-1 px-2 rounded-lg hover:bg-slate-50 font-bold flex items-center justify-between cursor-pointer group"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-[#DB2777] transition-colors" />
                        <span>นโยบายจัดส่ง คืนสินค้า และประกัน 7 วัน</span>
                      </span>
                      <span className="text-[10px] bg-rose-50 text-rose-700 font-bold px-1.5 py-0.5 rounded border border-rose-200">
                        7 วัน
                      </span>
                    </button>
                  </li>
                </ul>
              </div>

              {/* Column 2 (4 cols): Website Info & Trust Guarantee */}
              <div className="md:col-span-4 space-y-3">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#2A59FE]" />
                  <span>เกี่ยวกับ GIFFFORYOU.COM</span>
                </h4>
                <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                  เว็บไซต์สั่งซื้อผลิตภัณฑ์กิฟฟารีนออนไลน์อย่างเป็นทางการ สำหรับสมาชิก นักธุรกิจ และผู้บริโภคทั่วไป มั่นใจสินค้าแท้ 100% ส่งตรงจากคลังสินค้ากิฟฟารีนทั่วประเทศ รวดเร็ว ปลอดภัย พร้อมรับประกันความพึงพอใจ 7 วัน
                </p>
                <div className="pt-2 flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-[#2A59FE] text-[11px] font-bold border border-blue-200">
                    ✓ สินค้าแท้ 100%
                  </span>
                  <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-bold border border-emerald-200">
                    ✓ ประกันคืนเงิน 7 วัน
                  </span>
                </div>
              </div>

              {/* Column 3 (3 cols): Official Contact & LINE Channels */}
              <div className="md:col-span-3 space-y-3">
                <h4 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-700" />
                  <span>ติดต่อ & ปรึกษาสุขภาพ LINE OA</span>
                </h4>
                <div className="space-y-2.5">
                  <div className="flex flex-col gap-1.5">
                    <a 
                      href="https://lin.ee/PSM7qHqg" 
                      target="_blank" 
                      rel="noreferrer" 
                      className="inline-block transition-transform hover:scale-[1.03] active:scale-95"
                    >
                      <img 
                        src="https://scdn.line-apps.com/n/line_add_friends/btn/th.png" 
                        alt="เพิ่มเพื่อน" 
                        height="36" 
                        style={{ border: 0, height: '36px' }} 
                      />
                    </a>
                    <span className="text-xs font-mono font-bold text-slate-700">
                      LINE ID: <strong className="text-emerald-700">@997wvsow</strong>
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500 font-medium leading-relaxed">
                    บริการจัดส่งด่วนทั่วประเทศ Flash Express / Kerry / ไปรษณีย์ไทย ทุกวันจันทร์ - อาทิตย์
                  </p>
                </div>
              </div>

            </div>

            {/* Bottom Copyright Bar */}
            <div className="pt-5 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 font-medium gap-3">
              <p>© 2026 GIFFFORYOU.COM — Giffarine Official Online Store. All rights reserved.</p>
              <div className="flex flex-wrap items-center gap-3 text-[11px]">
                <button
                  onClick={() => {
                    window.history.pushState({}, '', '/privacy-policy');
                    window.dispatchEvent(new Event('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="hover:text-slate-900 hover:underline cursor-pointer"
                >
                  Privacy Policy
                </button>
                <span>•</span>
                <button
                  onClick={() => {
                    setPolicyModalTab('terms');
                    setIsPolicyOpen(true);
                  }}
                  className="hover:text-slate-900 hover:underline cursor-pointer"
                >
                  Terms of Service
                </button>
                <span>•</span>
                <button
                  onClick={() => {
                    setPolicyModalTab('pdpa');
                    setIsPolicyOpen(true);
                  }}
                  className="hover:text-slate-900 hover:underline cursor-pointer"
                >
                  PDPA Compliant
                </button>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Mobile Bottom Tab Bar (Increased Total Height by +15pt: min-h-[88px]) */}
      {!isHeaderFooterHidden && (
        <nav 
          style={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}
          className="fixed inset-x-0 bottom-0 z-50 md:hidden bg-gradient-to-r from-[#E0F2FE]/90 via-[#FFFFFF]/95 to-[#EDE9FE]/90 backdrop-blur-xl border-t border-white/90 shadow-[0_-10px_35px_rgba(42,89,254,0.12)] pt-4.5 pb-6 px-3 min-h-[88px] grid grid-cols-4 items-center justify-items-center"
        >
          {/* Subtle Pearlescent Lustre Highlight */}
          <div className="absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r from-sky-300/40 via-white/80 to-purple-300/40 pointer-events-none" />

          {/* Tab 1: Home */}
          <button
            onClick={() => {
              handleNavigateHome();
              setSelectedCategory('all');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`flex flex-col items-center justify-center w-full transition-all active:scale-90 cursor-pointer ${
              !currentProductSlug && !currentPathMemberId && !isAboutUsPage && selectedCategory === 'all'
                ? 'text-[#2A59FE]'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Home className="w-5.5 h-5.5 mb-1.5" />
            <span className="text-[11px] font-extrabold tracking-tight">หน้าแรก</span>
          </button>

          {/* Tab 2: Catalog */}
          <button
            onClick={() => {
              handleNavigateHome();
              setSelectedCategory('all');
              setTimeout(() => {
                const element = document.getElementById('products-display') || document.querySelector('.relative.rounded-\\[26px\\]');
                element?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className={`flex flex-col items-center justify-center w-full transition-all active:scale-90 cursor-pointer ${
              selectedCategory !== 'all' && !currentProductSlug && !currentPathMemberId
                ? 'text-[#2A59FE]'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Grid className="w-5.5 h-5.5 mb-1.5" />
            <span className="text-[11px] font-extrabold tracking-tight">แคตตาล็อก</span>
          </button>

          {/* Tab 3: Cart */}
          <button
            onClick={() => setIsCartOpen(true)}
            className={`flex flex-col items-center justify-center w-full relative transition-all active:scale-90 cursor-pointer ${
              isCartOpen ? 'text-[#2A59FE]' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <div className="relative">
              <ShoppingBag className="w-5.5 h-5.5 mb-1.5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1.5 bg-[#2A59FE] text-white text-[9.5px] font-black w-4.5 h-4.5 rounded-full flex items-center justify-center border border-white shadow-xs">
                  {cartCount}
                </span>
              )}
            </div>
            <span className="text-[11px] font-extrabold tracking-tight">ตะกร้า</span>
          </button>

          {/* Tab 4: Account/Member */}
          <button
            onClick={() => {
              if (currentUser) {
                if (currentUser.role === 'admin' || currentUser.role === 'seller') {
                  handleOpenSellerDashboard();
                } else {
                  setIsCustomerDashboardOpen(true);
                }
              } else {
                setIsAuthOpen(true);
              }
            }}
            className={`flex flex-col items-center justify-center w-full transition-all active:scale-90 cursor-pointer ${
              isCustomerDashboardOpen || isSellerAdminPage
                ? 'text-[#2A59FE]'
                : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <User className="w-5.5 h-5.5 mb-1.5" />
            <span className="text-[11px] font-extrabold tracking-tight">สมาชิก</span>
          </button>
        </nav>
      )}
    </div>
  );
}
